"use client"

import * as React from "react"

import { cn } from "../../lib/utils"
import { Button } from "./button"

const SCROLL_PIN_THRESHOLD_PX = 48

type MessageScrollerContextValue = {
  autoScroll: boolean
  isAtBottom: boolean
  scrollPreviousItemPeek: number
  setViewportEl: (el: HTMLDivElement | null) => void
  scrollToEnd: (behavior?: ScrollBehavior) => void
  notifyContentChange: () => void
  registerScrollAnchor: (el: HTMLElement | null) => void
  onViewportScroll: () => void
}

const MessageScrollerContext = React.createContext<MessageScrollerContextValue | null>(
  null,
)

function useMessageScrollerContext() {
  const ctx = React.useContext(MessageScrollerContext)
  if (!ctx) {
    throw new Error(
      "MessageScroller components must be used within MessageScrollerProvider",
    )
  }
  return ctx
}

/** Headless scroll state for a conversation transcript. */
function useMessageScroller() {
  return useMessageScrollerContext()
}

/** `true` when the viewport is not pinned to the latest content. */
function useMessageScrollerScrollable() {
  const { isAtBottom } = useMessageScrollerContext()
  return !isAtBottom
}

/** `true` when the jump-to-latest affordance should be visible. */
function useMessageScrollerVisibility() {
  const { isAtBottom } = useMessageScrollerContext()
  return !isAtBottom
}

function MessageScrollerProvider({
  autoScroll = true,
  scrollPreviousItemPeek = 48,
  children,
}: {
  autoScroll?: boolean
  scrollPreviousItemPeek?: number
  children: React.ReactNode
}) {
  const viewportRef = React.useRef<HTMLDivElement | null>(null)
  const isAtBottomRef = React.useRef(true)
  const [isAtBottom, setIsAtBottom] = React.useState(true)
  const latestAnchorRef = React.useRef<HTMLElement | null>(null)
  const shouldAnchorRef = React.useRef(false)

  const readAtBottom = React.useCallback((el: HTMLDivElement) => {
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight
    return distance <= SCROLL_PIN_THRESHOLD_PX
  }, [])

  const syncAtBottom = React.useCallback(
    (next: boolean) => {
      isAtBottomRef.current = next
      setIsAtBottom(next)
    },
    [],
  )

  const setViewportEl = React.useCallback(
    (el: HTMLDivElement | null) => {
      viewportRef.current = el
      if (el) syncAtBottom(readAtBottom(el))
    },
    [readAtBottom, syncAtBottom],
  )

  const scrollToEnd = React.useCallback((behavior: ScrollBehavior = "smooth") => {
    const el = viewportRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior })
    syncAtBottom(true)
  }, [syncAtBottom])

  const onViewportScroll = React.useCallback(() => {
    const el = viewportRef.current
    if (!el) return
    syncAtBottom(readAtBottom(el))
  }, [readAtBottom, syncAtBottom])

  const registerScrollAnchor = React.useCallback((el: HTMLElement | null) => {
    if (!el) return
    latestAnchorRef.current = el
    shouldAnchorRef.current = true
  }, [])

  const notifyContentChange = React.useCallback(() => {
    const el = viewportRef.current
    if (!el) return

    requestAnimationFrame(() => {
      if (shouldAnchorRef.current && latestAnchorRef.current) {
        const anchor = latestAnchorRef.current
        const viewportRect = el.getBoundingClientRect()
        const anchorRect = anchor.getBoundingClientRect()
        const delta =
          anchorRect.top - viewportRect.top - scrollPreviousItemPeek
        el.scrollBy({ top: delta, behavior: "smooth" })
        shouldAnchorRef.current = false
        syncAtBottom(readAtBottom(el))
        return
      }

      if (autoScroll && isAtBottomRef.current) {
        el.scrollTop = el.scrollHeight
        syncAtBottom(true)
      }
    })
  }, [autoScroll, readAtBottom, scrollPreviousItemPeek, syncAtBottom])

  const value = React.useMemo<MessageScrollerContextValue>(
    () => ({
      autoScroll,
      isAtBottom,
      scrollPreviousItemPeek,
      setViewportEl,
      scrollToEnd,
      notifyContentChange,
      registerScrollAnchor,
      onViewportScroll,
    }),
    [
      autoScroll,
      isAtBottom,
      scrollPreviousItemPeek,
      setViewportEl,
      scrollToEnd,
      notifyContentChange,
      registerScrollAnchor,
      onViewportScroll,
    ],
  )

  return (
    <MessageScrollerContext.Provider value={value}>
      {children}
    </MessageScrollerContext.Provider>
  )
}

function MessageScroller({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="message-scroller"
      className={cn(
        "group/message-scroller relative flex size-full min-h-0 flex-col overflow-hidden",
        className,
      )}
      {...props}
    />
  )
}

function MessageScrollerViewport({
  className,
  ref,
  onScroll,
  ...props
}: React.ComponentProps<"div">) {
  const { setViewportEl, onViewportScroll } = useMessageScrollerContext()

  const handleRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      setViewportEl(node)
      if (typeof ref === "function") ref(node)
      else if (ref) ref.current = node
    },
    [ref, setViewportEl],
  )

  return (
    <div
      ref={handleRef}
      data-slot="message-scroller-viewport"
      onScroll={(event) => {
        onViewportScroll()
        onScroll?.(event)
      }}
      className={cn(
        "size-full min-h-0 min-w-0 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]",
        className,
      )}
      {...props}
    />
  )
}

function MessageScrollerContent({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  const { notifyContentChange } = useMessageScrollerContext()

  React.useEffect(() => {
    notifyContentChange()
  }, [children, notifyContentChange])

  return (
    <div
      data-slot="message-scroller-content"
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      className={cn("flex h-max min-h-full flex-col gap-4", className)}
      {...props}
    >
      {children}
    </div>
  )
}

function MessageScrollerItem({
  className,
  scrollAnchor = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  scrollAnchor?: boolean
}) {
  const { registerScrollAnchor } = useMessageScrollerContext()
  const itemRef = React.useRef<HTMLDivElement | null>(null)

  React.useLayoutEffect(() => {
    if (!scrollAnchor || !itemRef.current) return
    registerScrollAnchor(itemRef.current)
  }, [registerScrollAnchor, scrollAnchor, children])

  return (
    <div
      ref={itemRef}
      data-slot="message-scroller-item"
      data-scroll-anchor={scrollAnchor ? "true" : undefined}
      className={cn("min-w-0 shrink-0", className)}
      {...props}
    >
      {children}
    </div>
  )
}

function MessageScrollerButton({
  direction = "end",
  className,
  children,
  variant = "secondary",
  size = "icon-sm",
  ...props
}: React.ComponentProps<"button"> & {
  direction?: "start" | "end"
  variant?: React.ComponentProps<typeof Button>["variant"]
  size?: React.ComponentProps<typeof Button>["size"]
}) {
  const { isAtBottom, scrollToEnd } = useMessageScrollerContext()
  const active = direction === "end" ? !isAtBottom : false

  return (
    <Button
      type="button"
      data-slot="message-scroller-button"
      data-direction={direction}
      data-active={active ? "true" : "false"}
      variant={variant}
      size={size}
      tabIndex={active ? 0 : -1}
      aria-hidden={!active}
      aria-label={direction === "end" ? "Scroll to latest message" : "Scroll to start"}
      onClick={() => scrollToEnd()}
      className={cn(
        "absolute start-1/2 z-10 -translate-x-1/2 border-border bg-background text-foreground shadow-sm transition-[translate,scale,opacity] duration-200 hover:bg-muted hover:text-foreground data-[active=false]:pointer-events-none data-[active=false]:scale-95 data-[active=false]:opacity-0 data-[direction=end]:bottom-4 data-[direction=start]:top-4 data-[direction=start]:[&_i]:rotate-180",
        className,
      )}
      {...props}
    >
      {children ?? (
        <>
          <i className="fa-light fa-arrow-down text-xs" aria-hidden="true" />
          <span className="sr-only">
            {direction === "end" ? "Scroll to latest message" : "Scroll to start"}
          </span>
        </>
      )}
    </Button>
  )
}

export {
  MessageScrollerProvider,
  MessageScroller,
  MessageScrollerViewport,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerButton,
  useMessageScroller,
  useMessageScrollerScrollable,
  useMessageScrollerVisibility,
}
